// src/pages/UploadTemplatePage.jsx
import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from './Header';

const UploadTemplatePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '', fields: [] });
  const url=process.env.REACT_APP_SERVER_URL;
  const navigate=useNavigate();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'text/html' || file.name.endsWith('.html'))) {
      setFormData({ ...formData, file });
      setMessage({ type: '', text: '', fields: [] });
    } else {
      setMessage({ 
        type: 'error', 
        text: 'Please select an HTML file',
        fields: []
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.file) {
      setMessage({ 
        type: 'error', 
        text: 'Please fill all fields',
        fields: []
      });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '', fields: [] });

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('file', formData.file);

      const response = await axios.post(`/api/templates/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setMessage({ 
        type: 'success', 
        text: 'Template uploaded successfully!',
        fields: response.data.template.fields
      });
      
      // Reset form
      setFormData({ name: '', file: null });
      document.getElementById('file-input').value = '';
      navigate('/templates');

    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || error.message,
        fields: []
      });
      console.log(error)
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
    <Header/>
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={12}
          sx={{
            padding: 4,
            borderRadius: 3
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <DescriptionIcon sx={{ fontSize: 60, color: '#667eea', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
              Upload Template
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload HTML template with editable fields marked as {'{{'}<strong>field_name</strong>{'}}'} 
            </Typography>
          </Box>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              
              {/* Template Name */}
              <TextField
                label="Template Name"
                placeholder="e.g., 3-Day Notice to Vacate"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                variant="outlined"
              />

              {/* File Upload */}
              <Box>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                  sx={{ 
                    padding: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    textTransform: 'none',
                    fontSize: 16
                  }}
                >
                  {formData.file ? formData.file.name : 'Choose HTML File'}
                  <input
                    id="file-input"
                    type="file"
                    accept=".html"
                    onChange={handleFileChange}
                    hidden
                    required
                  />
                </Button>
                
                {formData.file && (
                  <Alert 
                    icon={<CheckCircleIcon />} 
                    severity="success" 
                    sx={{ mt: 2 }}
                  >
                    {formData.file.name} ({(formData.file.size / 1024).toFixed(2)} KB)
                  </Alert>
                )}
              </Box>

              {/* Upload Progress */}
              {uploading && (
                <Box>
                  <LinearProgress />
                  <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                    Uploading...
                  </Typography>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={uploading}
                startIcon={<CloudUploadIcon />}
                sx={{
                  padding: 1.5,
                  fontSize: 16,
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  }
                }}
              >
                {uploading ? 'Uploading...' : 'Upload Template'}
              </Button>
            </Stack>
          </form>

          {/* Success/Error Messages */}
          {message.text && (
            <Box sx={{ mt: 3 }}>
              <Alert severity={message.type}>
                {message.text}
              </Alert>
              
              {message.fields && message.fields.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Fields Found ({message.fields.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {message.fields.map((field) => (
                      <Chip 
                        key={field} 
                        label={field} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

        </Paper>
      </Container>
    </Box>
    </>
  );
};

export default UploadTemplatePage;